<script lang="ts" setup>
  import type { ComplaintAndRefundAddress, Refund, RefundItem } from '@storefront/resources';

  interface Props {
    refund: Refund;
    refundReturnAddress: ComplaintAndRefundAddress;
    items: RefundItem[];
    showHeader?: boolean;
  }

  defineProps<Props>();

</script>

<template>
  <div class="refund-details">
    <h1 v-if="showHeader">
      {{ $t('SHOP.ACCOUNT.HEADER.REFUND_DETAILS') }}
    </h1>
    <SfTile>
      <template #title>
        <div class="refund-details__header-wrapper">
          <div class="refund-details__header">
            <h2 class="account__heading">
              {{ $t('SHOP.REFUND.HEADER.REFUND_NUMBER') }} {{ refund.number }}
            </h2>
            <span class="refund-details__date">
              {{ $t('SHOP.REFUND.HEADER.REFUND_DATE') }}
              <SfDateTime :value="refund.createdAt" />
            </span>
          </div>
          <div class="order__status">
            <SfRefundStatus :status="refund.status" />
          </div>
        </div>
      </template>
      <template #headerAction>

      </template>
      <template #default>
        <div class="refund-details__items">
          <SfListCard v-for="item in items" class="item" large>
            <template #image>
              <MediaPicture :photo="item.photo" sizes="140x140 2x, 70x70" />
            </template>
            <template #default>
              <div class="item__content">
                <h3 class="item__heading" v-text="item.title" />
                <p v-if="item.sku" class="item__sku" v-html="$t('SHOP.REFUND.LABEL.SKU', { code: item.sku })" />
                <p v-if="item.serialNumber" class="item__serial-number" v-html="$t('SHOP.REFUND.LABEL.SERIAL_NUMBER', { code: item.serialNumber })" />
              </div>
            </template>
          </SfListCard>
        </div>
      </template>
    </SfTile>

    <SfTile class="refund-details__data-info">
      <h2>{{ $t('SHOP.REFUND.HEADER.DETAILS') }}</h2>
      <div class="refund-details__data">
        <SfTile small>
          <template #title>
            <h3>{{ $t('SHOP.REFUND.HEADER.REFUND_CUSTOMER_DETAILS') }}</h3>
          </template>
          <address>
            <p v-if="refund.name">{{ refund.name }}</p>
            <p v-if="refund.firstLine">{{ refund.firstLine }}</p>
            <p v-if="refund.secondLine">{{ refund.secondLine }}</p>
            <p v-if="refund.thirdLine">{{ refund.thirdLine }}</p>
            <p v-if="refund.countryCode">{{ refund.countryCode }}</p>
            <p v-if="refund.email">{{ refund.email }}</p>
            <p v-if="refund.phone">{{ refund.phone }}</p>
          </address>
        </SfTile>
        <SfTile small>
          <template #title>
            <h3>{{ $t('SHOP.REFUND.HEADER.REFUND_DEFAULT_DATA') }}</h3>
          </template>
          <address>
            <p v-if="refundReturnAddress.company">{{ refundReturnAddress.company }}</p>
            <p v-if="refundReturnAddress.name">{{ refundReturnAddress.name }}</p>
            <p v-if="refundReturnAddress.firstLine">{{ refundReturnAddress.firstLine }}</p>
            <p v-if="refundReturnAddress.secondLine">{{ refundReturnAddress.secondLine }}</p>
            <p v-if="refundReturnAddress.thirdLine">{{ refundReturnAddress.thirdLine }}</p>
            <p v-if="refundReturnAddress.countryCode">{{ refundReturnAddress.countryCode }}</p>
            <p v-if="refundReturnAddress.email">{{ refundReturnAddress.email }}</p>
            <p v-if="refundReturnAddress.phone">{{ refundReturnAddress.phone }}</p>
          </address>
        </SfTile>

        <SfTile small v-if="refund.reason">
          <template #title>
            <h3>{{ $t('SHOP.REFUND.HEADER.REFUND_DETAILS') }}</h3>
          </template>
          <p><strong>{{ $t('SHOP.REFUND.LABEL.REASON') }}</strong> {{ refund.reason }}</p>
        </SfTile>

        <SfTile small>
          <template #title>
            <h3>{{ $t('SHOP.REFUND.HEADER.REFUND_REASON') }}</h3>
          </template>
          <p><strong>{{ $t('SHOP.REFUND.LABEL.REFUND_METHOD') }}</strong> {{ refund.returnMethod.name }}</p>
          <p><strong>{{ $t('SHOP.REFUND.LABEL.REFUND_METHOD_DESCRIPTION') }}</strong> {{ refund.returnMethod.description }}</p>
        </SfTile>
      </div>
      <SfTile small v-if="refund.files && refund.files?.length > 0">
        <template #title>
          <h3>{{ $t('SHOP.REFUND.HEADER.ATTACHMENTS') }}</h3>
        </template>
        <ul class="refund-details__files">
          <li v-for="file in refund.files">
            <a :href="file.raw || $route('public.media.file.download', { filename: file.filename })" target="_blank">
              <span class="icon">
                <SfIconDocument aria-hidden="true" />
              </span>
              <span class="name">
                {{ file.displayName || file.original || file.filename }}
              </span>
            </a>
          </li>
        </ul>
      </SfTile>
    </SfTile>
    <RefundChat :refund />
  </div>
</template>

<style lang="scss" scoped>
  @use "@storefront/styles/mixins/media";

  .refund-details {
    --sf-tile-small-padding: 0;
    --sf-tile-small-gap: 0.5rem;
    --sf-tile-gap: 2rem;

    display: flex;
    flex-direction: column;
    row-gap: 2rem;

    h3 {
      font-size: 1.125rem;
    }

    p {
      font-size: 0.875rem;
    }

    &__data {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      align-items: flex-start;
      gap: 2rem;

      @include media.query(xl) {
        grid-template-columns: repeat(3, 1fr);
      }

      @include media.query(md) {
        grid-template-columns: repeat(1, 1fr);
        gap: 1rem;
      }
    }

    &__items {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      :deep(img) {
        aspect-ratio: 1;
        width: 70px;
      }

      @include media.query(md) {
        gap: 0.5rem;
      }
    }

    &__header-wrapper {
      display: flex;
      justify-content: space-between;
      gap: 1rem;
      width: 100%;
    }

    &__files {
      line-height: 1.5rem;
      list-style: none;
      padding: 0;
      margin: 0;

      a {
        display: flex;
        align-items: flex-start;
        line-height: 1rem;
        font-size: 0.875rem;
        gap: 0.5rem;

        svg {
          display: inline-block;
          font-size: 1rem;
        }
      }
    }

    &__data-info > :deep(.tile__content) {
      gap: 1.5rem;
    }

    @include media.query(md) {
      gap: 1rem;
      h3 {
        font-size: 1rem;
      }

      &__header-wrapper {
        flex-direction: column;
        justify-content: flex-start;
      }

      &__data-info > :deep(.tile__content) {
        gap: 0.75rem;
      }
    }
  }

  .item {
    &__heading {
      font-weight: var(--font-weight-bold);
      font-size: 1rem;
    }

    &__sku,
    &__serial-number {
      font-size: 0.75rem;
      color: var(--secondary-color);
    }
  }
</style>
